import { PmoKpiConfig } from './pmoKpiConfig';

export const COMPRAS_E_KPI_STORAGE_KEY = 'projects_compras_e_kpi_configs_v4';
const COMPRAS_E_KPI_LEGACY_STORAGE_KEYS = [
  'projects_compras_e_kpi_configs_v3',
  'projects_compras_e_kpi_configs_v2',
  'projects_compras_e_kpi_configs_v1',
];

export type ComprasEKpiOption = {
  id: string;
  label: string;
  description: string;
};

export const COMPRAS_E_KPI_OPTIONS: ComprasEKpiOption[] = [
  {
    id: 'issue_count',
    label: 'Itens com apontamento',
    description: 'Itens que receberam horas de trabalho dentro do período.',
  },
  {
    id: 'parent_count',
    label: 'Demandas com apontamento',
    description: 'Demandas principais que tiveram trabalho no período. Não é quantidade de horas.',
  },
  {
    id: 'subtask_count',
    label: 'Subtarefas Compras-e',
    description: 'Tarefas previstas do Compras-e, incluindo as que ainda não começaram.',
  },
  {
    id: 'portal_hours',
    label: 'HH Portal de Compras',
    description: 'Horas do Compras-e realizadas dentro do Portal de Compras.',
  },
  {
    id: 'cmpe_hours',
    label: 'HH CMPE',
    description: 'Horas realizadas na frente própria do Compras-e.',
  },
  {
    id: 'sap_hours',
    label: 'HH SAP',
    description: 'Parte das horas do Portal dedicada ao SAP. Já está incluída no HH Real.',
  },
  {
    id: 'total_hours',
    label: 'HH Real (período)',
    description: 'Total de horas trabalhadas no período, somando as duas frentes.',
  },
  {
    id: 'sold_hours',
    label: 'HH Vendido',
    description: 'Horas contratadas das demandas que tiveram trabalho no período.',
  },
  {
    id: 'consumo',
    label: '% Consumo (período)',
    description: 'Quanto das horas contratadas já foi consumido pelo trabalho realizado.',
  },
  {
    id: 'custo_total',
    label: 'Custo total (período)',
    description: 'Valor das horas trabalhadas no período.',
  },
  {
    id: 'custo_proprio',
    label: 'Custo próprio',
    description: 'Parte do custo referente a profissionais da própria empresa.',
  },
  {
    id: 'custo_terceiro',
    label: 'Custo terceiro',
    description: 'Parte do custo referente a profissionais terceirizados.',
  },
];

export const COMPRAS_E_KPI_OPTIONS_BY_ID: Record<string, ComprasEKpiOption> = (
  COMPRAS_E_KPI_OPTIONS.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as Record<string, ComprasEKpiOption>)
);

/** Cards de contagem auxiliares: existem no catálogo, mas só aparecem se o usuário marcar. */
export const COMPRAS_E_OPTIONAL_KPI_IDS = ['issue_count', 'subtask_count'];

export const COMPRAS_E_DEFAULT_KPI_IDS = COMPRAS_E_KPI_OPTIONS
  .map((item) => item.id)
  .filter((id) => !COMPRAS_E_OPTIONAL_KPI_IDS.includes(id));

export const COMPRAS_E_COST_KPI_IDS = ['custo_total', 'custo_proprio', 'custo_terceiro'];

export const COMPRAS_E_DEFAULT_KPI_CONFIGS: PmoKpiConfig[] = COMPRAS_E_DEFAULT_KPI_IDS.map((id) => ({ id }));

/** Layout antigo gravava Subtarefas no lugar dos pais. Troca só se o card novo ainda não estiver na lista. */
export const replaceLegacySubtaskWithParent = (configs: PmoKpiConfig[]): PmoKpiConfig[] => {
  if (configs.some((item) => item.id === 'parent_count')) return configs;
  return configs.map((item) => (item.id === 'subtask_count' ? { id: 'parent_count' } : item));
};

/** Itens com apontamento virou card opcional: some do layout salvo antes dessa mudança. */
export const dropLegacyIssueCountCard = (configs: PmoKpiConfig[]): PmoKpiConfig[] => (
  configs.filter((item) => item.id !== 'issue_count')
);

/** Acrescenta cards novos (custos) sem apagar o layout que o usuário já salvou. */
export const appendMissingCatalogKpis = (configs: PmoKpiConfig[]): PmoKpiConfig[] => {
  if (!configs.length) return configs;
  const seen = new Set(configs.map((item) => item.id));
  const extra = COMPRAS_E_COST_KPI_IDS
    .filter((id) => COMPRAS_E_KPI_OPTIONS_BY_ID[id] && !seen.has(id))
    .map((id) => ({ id }));
  return extra.length ? [...configs, ...extra] : configs;
};

export const sanitizeComprasEKpiConfigs = (
  value: unknown,
  fallbackWhenEmpty = true,
): PmoKpiConfig[] => {
  const asArray = Array.isArray(value) ? value : [];
  const out: PmoKpiConfig[] = [];
  const seen = new Set<string>();
  asArray.forEach((entry) => {
    let id = '';
    let title = '';
    if (typeof entry === 'string') {
      id = entry.trim();
    } else if (entry && typeof entry === 'object') {
      const obj = entry as { id?: unknown; title?: unknown };
      id = typeof obj.id === 'string' ? obj.id.trim() : '';
      title = typeof obj.title === 'string' ? obj.title.trim() : '';
    }
    if (!id || !COMPRAS_E_KPI_OPTIONS_BY_ID[id] || seen.has(id)) return;
    seen.add(id);
    out.push({ id, title: title || undefined });
  });
  if (!out.length) {
    return fallbackWhenEmpty ? COMPRAS_E_DEFAULT_KPI_CONFIGS.map((item) => ({ ...item })) : [];
  }
  return out;
};

export const applyComprasEKpiConfigs = <T extends { id: string; label: string }>(
  items: T[],
  configs: PmoKpiConfig[],
): T[] => {
  const byId = new Map(items.map((item) => [item.id, item]));
  return configs.flatMap((config) => {
    const item = byId.get(config.id);
    if (!item) return [];
    const customTitle = String(config.title || '').trim();
    return [{
      ...item,
      label: customTitle || item.label,
    }];
  });
};

const parseStoredKpiConfigs = (raw: string | null, fromLegacyKey = false): PmoKpiConfig[] | null => {
  if (raw == null) return null;
  const parsed = JSON.parse(raw) as unknown;
  if (Array.isArray(parsed) && parsed.length === 0) {
    return [];
  }
  const sanitized = sanitizeComprasEKpiConfigs(parsed, true);
  const migrated = fromLegacyKey ? dropLegacyIssueCountCard(sanitized) : sanitized;
  return appendMissingCatalogKpis(replaceLegacySubtaskWithParent(migrated));
};

export const loadComprasEKpiConfigs = (): PmoKpiConfig[] => {
  if (typeof window === 'undefined') {
    return COMPRAS_E_DEFAULT_KPI_CONFIGS.map((item) => ({ ...item }));
  }
  try {
    const current = parseStoredKpiConfigs(window.localStorage.getItem(COMPRAS_E_KPI_STORAGE_KEY));
    if (current) return current;
    for (const legacyKey of COMPRAS_E_KPI_LEGACY_STORAGE_KEYS) {
      const migrated = parseStoredKpiConfigs(window.localStorage.getItem(legacyKey), true);
      if (migrated) return migrated;
    }
    return COMPRAS_E_DEFAULT_KPI_CONFIGS.map((item) => ({ ...item }));
  } catch {
    return COMPRAS_E_DEFAULT_KPI_CONFIGS.map((item) => ({ ...item }));
  }
};

export const saveComprasEKpiConfigs = (configs: PmoKpiConfig[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COMPRAS_E_KPI_STORAGE_KEY, JSON.stringify(configs));
  } catch {
    // quota / private mode — ignore
  }
};
